// ranking_preview.js
// Purpose: Non-blocking preview for Ranking Reports.
// What it does:
// - Posts start_preview to enqueue a background build (Sidekiq).
// - Shows a small “Building preview…” banner immediately.
// - Listens for ActionCable notifications via NotificationsChannel (handled elsewhere)
//   and in parallel polls /preview_status as a fallback (“race” strategy).
// - When ready, fetches server-rendered HTML via /preview_html and injects it into #container.
// Notes:
// - Config is provided by a <script type="application/json" id="ranking-preview-config"> tag.
// - Polling runs every 500ms up to ~150s; push notifications typically resolve sooner.

// Ensure jQuery is available as a global for legacy code that expects `$`/`jQuery`.
const $ = require("jquery");
require("channels");
if (typeof window !== 'undefined') {
  window.$ = $;
  window.jQuery = $;
}

function getMeta(name) {
  const el = document.querySelector(`meta[name="${name}"]`);
  return el && el.getAttribute('content');
}

let lastPreviewError = null;

function showStatus(text, isError) {
  const bar = document.getElementById('preview-status');
  const txt = document.getElementById('preview-status-text');
  if (!bar || !txt) return;
  const message = text || lastPreviewError || 'Building preview…';
  txt.textContent = message;
  bar.style.display = 'block';
  if (isError) {
    bar.style.backgroundColor = '#c92a1e';
  }
}

function showError(message) {
    lastPreviewError = message || 'Error building preview';
    showStatus(lastPreviewError, true);
  }

function hideStatus() {
  const bar = document.getElementById('preview-status');
  if (bar) bar.style.display = 'none';
  lastPreviewError = null;
}

async function postJSON(url, body) {
  const token = getMeta('csrf-token');
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token
    },
    body: JSON.stringify(body || {})
  });
  return res.json();
}

async function getJSON(url) {
  const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
  return res.json();
}

async function pollStatusUntilReady(statusUrl) {
  let attempts = 0;
  while (attempts < 300) { // up to ~150s at 500ms interval
    const j = await getJSON(statusUrl);
    if (j.status === 'ready') return { ready: true, error: null };
    if (j.status === 'error') return { ready: false, error: j.error || 'Error building preview' };
    if (j.status === 'not_started') return { ready: false, error: null };
    await new Promise(r => setTimeout(r, 500));
    attempts++;
  }
  return { ready: false, error: null };
}

async function renderFromPreviewData() {
  const container = document.getElementById('container');
  if (!container) return;
  const res = await fetch(window.rankingPreviewConfig.htmlUrl, { headers: { 'Accept': 'text/html' } });
  if (!res.ok) throw new Error('Preview HTML not ready');
  const html = await res.text();
  container.innerHTML = html;
}

window.RankingPreviewClient = {
  async start() {
    try {
      if (!window.rankingPreviewConfig) {
        const cfgEl = document.getElementById('ranking-preview-config');
        if (cfgEl) {
          try { window.rankingPreviewConfig = JSON.parse(cfgEl.textContent || cfgEl.innerText || '{}'); } catch(e) {}
        }
      }
      showStatus('Building preview…');
      await postJSON(window.rankingPreviewConfig.startUrl, {});

      // Race notifications with polling as a fallback
      const pollResult = await pollStatusUntilReady(window.rankingPreviewConfig.statusUrl);
      if (pollResult.ready) {
        await renderFromPreviewData();
        hideStatus();
      } else if (pollResult.error) {
        lastPreviewError = pollResult.error;
        showError(lastPreviewError);
      } else if (lastPreviewError) {
        showError(lastPreviewError);
      } else {
        console.log("Ranking preview: polling timed out, waiting for push notification");
        showStatus('Building preview…');
      }
    } catch (e) {
      console.error(e);
      if (lastPreviewError) {
        showError(lastPreviewError);
      } else {
        showError('Error building preview');
      }
    }
  },
  async onReady() {
    try {
      if (!window.rankingPreviewConfig) {
        const cfgEl = document.getElementById('ranking-preview-config');
        if (cfgEl) {
          try { window.rankingPreviewConfig = JSON.parse(cfgEl.textContent || cfgEl.innerText || '{}'); } catch(e) {}
        }
      }
      await renderFromPreviewData();
      hideStatus();
    } catch (e) {
      console.error(e);
      showStatus('Error loading preview');
    }
  },
  onError(msg) {
    lastPreviewError = msg || 'Error building preview';
    console.error(lastPreviewError);
    showError(lastPreviewError);
  }
}
